'use client'

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ParseKeys } from 'i18next'
import { AnimatePresence, motion, MotionConfig } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { setI18nNamespace, tKey } from '@/utils/i18n'
import { Text } from 'ui/src/components/Text/Text'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { type FlowProps } from '../flows.types'
import { HedvigChatIcon } from './HedvigChatIcon'
import {
  bubbleRow,
  chipMotion,
  chipsContainer,
  chipsWrap,
  conversation,
  header,
  iconRow,
  pill,
  root,
} from './Flow04Claims.css'

setI18nNamespace('appShowcase')

type AppShowcaseKey = ParseKeys<'appShowcase'>

type TurnConfig = {
  id: string
  questionKey: AppShowcaseKey
  chipKeys: Array<AppShowcaseKey>
  selectedKey: AppShowcaseKey
}

type ResolvedTurn = {
  id: string
  question: string
  chips: Array<string>
  selected: string
}

const TURN_CONFIGS: Array<TurnConfig> = [
  {
    id: 'turn-1',
    questionKey: tKey<'appShowcase'>('FLOW_04_TURN_1_QUESTION'),
    chipKeys: [
      tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_MYSELF'),
      tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_FAMILY'),
      tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_BELONGINGS'),
      tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_TRIP'),
      tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_HOME'),
      tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_OTHER'),
    ],
    selectedKey: tKey<'appShowcase'>('FLOW_04_TURN_1_CHIP_BELONGINGS'),
  },
  {
    id: 'turn-2',
    questionKey: tKey<'appShowcase'>('FLOW_04_TURN_2_QUESTION'),
    chipKeys: [
      tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_COMPUTER'),
      tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_PHONE'),
      tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_GLASSES'),
      tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_JEWELLERY'),
      tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_TABLET'),
      tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_HEADPHONES'),
    ],
    selectedKey: tKey<'appShowcase'>('FLOW_04_TURN_2_CHIP_PHONE'),
  },
]

// Named rhythm constants — edit here to re-time the whole sequence
const CHAR_SPEED = 0.02 // seconds per character
const CHIP_STAGGER = 0.06 // seconds between chips
const PRESS_DURATION = 0.2 // seconds
const TEXT_LEAD = 0.6 // icon → text delay
const CHIP_LEAD = 0.2 // text-done → first chip delay
const DWELL = 0.8 // all chips visible → press delay
const BUBBLE_LEAD = 0.2 // chips-exit → bubble delay
const TURN_GAP = 0.8 // bubble-complete → next turn delay

type TurnTiming = {
  textDelay: number
  textDuration: number
  chipsDelay: number
  pressDelay: number
}

function getTurnTiming(questionLength: number, chipCount: number): TurnTiming {
  const textDelay = TEXT_LEAD
  const textDuration = questionLength * CHAR_SPEED
  const chipsDelay = textDelay + textDuration + CHIP_LEAD
  const pressDelay = chipsDelay + chipCount * CHIP_STAGGER + DWELL
  return { textDelay, textDuration, chipsDelay, pressDelay }
}

const spring = { type: 'spring' as const, stiffness: 400, damping: 30, mass: 0.8 }

export function Flow04Claims(_props: FlowProps) {
  const { t } = useTranslation('appShowcase')
  const [visibleTurns, setVisibleTurns] = useState(1)
  const [dismissedChipsCount, setDismissedChipsCount] = useState(0)
  const turnGapTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

  const turns = useMemo<Array<ResolvedTurn>>(
    () =>
      TURN_CONFIGS.map((config) => ({
        id: config.id,
        question: t(config.questionKey),
        chips: config.chipKeys.map((chipKey) => t(chipKey)),
        selected: t(config.selectedKey),
      })),
    [t],
  )

  const turnTimings = useMemo(
    () => turns.map((turn) => getTurnTiming(turn.question.length, turn.chips.length)),
    [turns],
  )

  useEffect(() => () => clearTimeout(turnGapTimeout.current), [])

  // Driven by the selected chip's onAnimationComplete — one clock, no absolute offsets
  const handlePressComplete = useCallback((turnIndex: number) => {
    setDismissedChipsCount(turnIndex + 1)
  }, [])

  // Driven by the bubble's onAnimationComplete — advances the conversation after TURN_GAP
  const handleBubbleComplete = useCallback((turnIndex: number) => {
    if (turnIndex < TURN_CONFIGS.length - 1) {
      turnGapTimeout.current = setTimeout(() => setVisibleTurns(turnIndex + 2), TURN_GAP * 1000)
    }
  }, [])

  const activeChipsTurnIndex = dismissedChipsCount < visibleTurns ? dismissedChipsCount : -1

  return (
    <MotionConfig reducedMotion="user">
      <div className={root}>
        <div className={header}>
          <Text as="span" size="sm" color="textPrimary">
            {t('FLOW_04_HEADER')}
          </Text>
        </div>

        <div className={conversation}>
          {turns.slice(0, visibleTurns).map((turn, turnIndex) => {
            const timing = turnTimings[turnIndex]
            const isDismissed = turnIndex < dismissedChipsCount

            return (
              <Fragment key={turn.id}>
                <div className={iconRow}>
                  <HedvigChatIcon />
                </div>

                <motion.div
                  initial={{ clipPath: 'inset(0 100% 0 0)' }}
                  animate={{ clipPath: 'inset(0 0% 0 0)' }}
                  transition={{ delay: timing.textDelay, duration: timing.textDuration, ease: 'linear' }}
                >
                  <Text
                    as="p"
                    size={{ _: 'xxs', md: 'xs' }}
                    color="textPrimary"
                    className={sprinkles({ marginBottom: 'xxs' })}
                  >
                    {turn.question}
                  </Text>
                </motion.div>

                {isDismissed && (
                  <motion.div
                    className={bubbleRow}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...spring, delay: BUBBLE_LEAD }}
                    onAnimationComplete={() => handleBubbleComplete(turnIndex)}
                  >
                    <Text as="div" className={pill} size={{ _: 'xxs', md: 'xs' }} color="textPrimary">
                      {turn.selected}
                    </Text>
                  </motion.div>
                )}
              </Fragment>
            )
          })}
        </div>

        {/* mode="wait" ensures a departing chip set fully exits before a new one can enter */}
        <AnimatePresence mode="wait">
          {activeChipsTurnIndex >= 0 && (
            <motion.div
              key={`chips-${activeChipsTurnIndex}`}
              className={chipsContainer}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className={chipsWrap}>
                {turns[activeChipsTurnIndex].chips.map((chip, chipIndex) => {
                  const isSelected = chip === turns[activeChipsTurnIndex].selected
                  const timing = turnTimings[activeChipsTurnIndex]
                  const chipDelay = timing.chipsDelay + chipIndex * CHIP_STAGGER

                  return (
                    <motion.div
                      key={chip}
                      className={chipMotion}
                      initial={{ opacity: 0 }}
                      // Selected chip presses down; the container exit handles the fade-out
                      animate={isSelected ? { opacity: 1, scale: 0.92 } : { opacity: 1 }}
                      transition={
                        isSelected
                          ? {
                              opacity: { delay: chipDelay, duration: 0.2, ease: 'easeOut' },
                              scale: { delay: timing.pressDelay, duration: PRESS_DURATION },
                            }
                          : { delay: chipDelay, duration: 0.2, ease: 'easeOut' }
                      }
                      onAnimationComplete={isSelected ? () => handlePressComplete(activeChipsTurnIndex) : undefined}
                    >
                      <div className={pill}>
                        <Text as="span" size={{ _: 'xxs', md: 'xs' }} color="textPrimary">
                          {chip}
                        </Text>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  )
}
